import { useMutation } from '@tanstack/react-query';
import { MarketingCampaigns } from '@prisma/client';
import { ValidationError } from 'yup';
import useGetMarketingData from './useGetMarketingData';
import { upsertCampaign } from '../../services/marketing';
import {
  CreateUpdateCampaignPayload,
  getUpsertCampaignSchema,
  MarketingCampaignWithSource,
} from '../../types/marketing';
import useGetEmailTemplates from './useGetEmailTemplates';
import useGetSmsTemplates from './useGetSmsTemplates';

interface Payload {
  body: CreateUpdateCampaignPayload;
  draft?: boolean;
}

interface Options {
  onSuccess?: (campaign: MarketingCampaigns) => void;
  onError?: (message?: string) => void;
  onMutate?: (variables: Payload) => void;
  onSettled?: () => void;
}

function useUpsertCampaign(
  campaign?: MarketingCampaignWithSource | null,
  { onSuccess, onError, onMutate, onSettled }: Options = {},
) {
  const { data, isFetching } = useGetMarketingData();
  const { data: { templates: emailTemplates = [] } = {} } =
    useGetEmailTemplates({
      enabled: false,
    });
  const { data: { templates: smsTemplates = [] } = {} } = useGetSmsTemplates({
    enabled: false,
  });

  return useMutation({
    mutationFn: async ({ body, draft }: Payload) => {
      if (!data?.id || isFetching) return null;

      const content = draft
        ? body.medium === 'SMS' && 'smsTemplateId' in body.content
          ? body.content
          : body.medium === 'EMAIL' && 'emailTemplateId' in body.content
          ? body.content
          : null
        : body.content;

      const vals = {
        id: campaign?.id,
        ...body,
        content,
      } as CreateUpdateCampaignPayload;

      const schema = draft
        ? getUpsertCampaignSchema(emailTemplates, smsTemplates, true)
        : getUpsertCampaignSchema(emailTemplates, smsTemplates);

      const payload = await schema.noUnknown().validate(vals, {
        stripUnknown: true,
      });

      const result = await upsertCampaign(data?.id, payload, draft);
      return result;
    },
    onSuccess: async result => {
      if (!result || 'error' in result)
        throw new Error(result?.error || 'Failed to create campaign');
      if (onSuccess) onSuccess(result.campaign);
    },
    onError: (err: any) => {
      if ('message' in err && err?.message === 'No customers found') {
        onError?.('No customers were found for the selected segments');
        return;
      }
      if (err instanceof ValidationError) {
        onError?.(err.message);
        return;
      }
      onError?.('Failed to create campaign');
    },
    onMutate,
    onSettled,
  });
}

export default useUpsertCampaign;
